import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { fetchIdeaFeedback } from '../../api/ideaFeedback';
import { fetchIdeas } from '../../api/idea';
import { SubmissionProgress } from './mentor-review/SubmissionProgress';

export function MentorReview() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { ideaId, ideaTitle } = (location.state as { ideaId?: string; ideaTitle?: string } | null) ?? {};

  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [stats, setStats] = useState<{ averageRating: number | null; ratingCount: number } | null>(null);
  const [ideaStatus, setIdeaStatus] = useState<'submitted_initial' | 'under_mentor_review' | 'submitted_final' | 'selected' | 'rejected' | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !ideaId) { setLoading(false); return; }
    
    // Fetch both feedback and idea details in parallel
    Promise.all([
      fetchIdeaFeedback(token, ideaId).then((json) => {
        setFeedbacks(json.items ?? []);
        setStats(json.stats ?? null);
      }),
      fetchIdeas(token).then((data) => {
        // Find the specific idea by ideaId
        const ideas = data.items ?? [];
        const currentIdea = ideas.find((i: any) => i._id === ideaId);
        if (currentIdea) {
          setIdeaStatus(currentIdea.status);
        }
      })
    ])
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token, ideaId]);

  const fmt = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Back */}
          <button
            onClick={() => navigate('/user-dashboard/idea-list')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors mb-6"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Idea List
          </button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl text-gray-900 mb-2">Mentor Review</h1>
            {ideaTitle && <p className="text-lg text-blue-600 font-medium">{ideaTitle}</p>}
            <p className="text-base text-gray-500 mt-1">Feedback from mentors on your submission</p>
          </div>

          <div className="space-y-6">
            <SubmissionProgress ideaStatus={ideaStatus} />

            {/* Feedback Section */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl">Mentor Feedback</h2>
                {stats && stats.ratingCount > 0 && (
                  <div className="flex items-center gap-2 bg-cyan-50 border border-cyan-100 px-4 py-2 rounded-xl">
                    <span className="text-yellow-500 text-lg">★</span>
                    <span className="text-gray-800 font-semibold">{stats.averageRating}</span>
                    <span className="text-gray-400 text-sm">/ 5 ({stats.ratingCount} review{stats.ratingCount !== 1 ? 's' : ''})</span>
                  </div>
                )}
              </div>

              {loading ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-24 bg-gray-100 rounded-xl" />
                  <div className="h-24 bg-gray-100 rounded-xl" />
                </div>
              ) : feedbacks.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  No mentor feedback yet for this idea.
                </div>
              ) : (
                <div className="space-y-4">
                  {feedbacks.map((fb, i) => (
                    <div key={fb._id ?? i} className="bg-cyan-50 rounded-xl p-6 border border-cyan-100">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">
                            {fb.mentorId?.name ?? 'Mentor'}
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5">{fmt(fb.createdAt)}</p>
                        </div>
                        {fb.rating != null && (
                          <div className="flex items-center gap-1 bg-white border border-cyan-200 px-3 py-1 rounded-lg">
                            <span className="text-yellow-500">★</span>
                            <span className="text-sm font-semibold text-gray-700">{fb.rating}/5</span>
                          </div>
                        )}
                      </div>
                      <span className="inline-block bg-cyan-200 text-cyan-900 text-xs px-3 py-1 rounded-lg mb-3 capitalize">
                        {fb.phase?.replace('_', ' ') ?? 'Review'}
                      </span>
                      <p className="text-gray-700 leading-relaxed text-sm">{fb.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
